import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, MenuItem, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { RHFSelect, RHFTextField, RHFUpload } from "../../components/hook-form";
import FormProvider from "../../components/hook-form/FormProvider";
import RHFJoditEditor from "../../components/hook-form/RHFJoditEditor";
import { Upload } from "../../components/upload";
import { CountryData } from "../../data/countryData";
import { fetchCategories } from "../../redux/slices/categorySlice";
import { createProject, updateProject } from "../../redux/slices/projectSlice";
import { slug } from "../../utils/slug";

const ProjectForm = ({ handleClose, data, isEdit = false }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [image, setImage] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(""); // Province state
  const [districts, setDistricts] = useState([]); // Districts based on the selected province

  const createProjectLoading = useSelector((state) => state.project.isLoading);

  const Schema = Yup.object().shape({
    title: Yup.string().required("Project title is required"),
    description: Yup.string().required("Project description is required"),
    province: Yup.string().required("Project province is required"),
    district: Yup.string().required("Project district is required"),
  });

  const defaultValues = useMemo(
    () => ({
      title: data?.title || "",
      artists: data?.artists || "",
      thumbnail: `${process.env.REACT_APP_HOST}${data?.thumbnail}` || "",
      image:
        data?.image?.map((img) => `${process.env.REACT_APP_HOST}${img}`) || "",
      description: data?.description || "",
      released_date: data?.released_date ? dayjs(data?.released_date) : dayjs(),
      province: data?.province || "",
      district: data?.district || "",
    }),
    [data]
  );

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const { handleSubmit, watch, setValue } = methods;
  const values = watch();

  useEffect(() => {
    dispatch(fetchCategories({ limit: 100, project: 0, enqueueSnackbar }));
  }, [dispatch, enqueueSnackbar]);

  // Handle province selection and update districts
  const handleProvinceChange = useCallback(
    (provinceName) => {
      setSelectedProvince(provinceName);
      const selectedProvinceObj = CountryData.provinceList.find(
        (province) => province.name === provinceName
      );
      setDistricts(selectedProvinceObj ? selectedProvinceObj.districtList : []);
      setValue("district", ""); // Reset district selection when province changes
    },
    [setValue]
  );

  // Set districts based on default province when the form is initialized
  useEffect(() => {
    if (defaultValues.province) {
      handleProvinceChange(defaultValues.province);
      setValue("district", defaultValues.district); // Set the district value explicitly
    }
  }, [
    defaultValues.province,
    defaultValues.district,
    handleProvinceChange,
    setValue,
  ]);

  const handleDropImage = useCallback((acceptedFiles) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      setImage(
        Object.assign(newFile, { preview: URL.createObjectURL(newFile) })
      );
      setImage(acceptedFiles[0]);
    }
  }, []);

  const handleDropPhoto = useCallback(
    (acceptedFiles) => {
      const files = values.image || [];
      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      );

      setGallery((prev) => [...prev, ...newFiles]);
      setValue("image", [...files, ...newFiles], { shouldValidate: true });
    },
    [setValue, values]
  );

  const onCreateProject = (values) => {
    delete values.thumbnail;
    const formData = new FormData();
    formData.append("title", values.title);

    formData.append("description", values.description);
    formData.append("province", values.province);
    formData.append("district", values.district);

    if (image) {
      formData.append("thumbnail", image);
    }
    if (values?.image?.length) {
      values.image.forEach((file) => {
        formData.append("image", file);
      });
    }

    dispatch(createProject({ data: formData, enqueueSnackbar, handleClose }));
  };

  const onUpdateProject = (values) => {
    delete values.image;

    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("slug", slug(values.title));

    formData.append("description", values.description);
    formData.append("province", values.province);
    formData.append("district", values.district);

    if (image) {
      formData.append("thumbnail", image);
    }
    if (gallery?.length) {
      gallery.forEach((file) => {
        formData.append("image", file);
      });
    }

    dispatch(
      updateProject({
        data: formData,
        enqueueSnackbar,
        handleClose,
        id: data?.id,
      })
    );
  };

  const handleRemoveFile = (inputFile) => {
    const extractedText = inputFile.match(/(uploads\/.*)/)?.[1];
    // Remove the file from the state
    const filteredImages = values.image?.filter((file) => file !== inputFile);
    setValue("image", filteredImages);
    // Dispatch the update action, sending only the imageToRemove field
    if (inputFile) {
      dispatch(
        updateProject({
          image: true,
          data: { imageToRemove: extractedText },
          enqueueSnackbar,
          handleClose,
          id: data?.id,
        })
      );
    }
  };

  return (
    <Box p={3}>
      <FormProvider
        methods={methods}
        onSubmit={handleSubmit(isEdit ? onUpdateProject : onCreateProject)}
      >
        <Stack flexDirection={"row"} justifyContent={"center"} mb={2}></Stack>
        <Box
          rowGap={3}
          columnGap={2}
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(1, 1fr)",
            sm: "repeat(1, 1fr)",
          }}
        >
          <RHFTextField name={"title"} label={"Title *"} />
        </Box>

        <Box mt={3}>
          <p className="mb-3">Project's description *</p>
          <RHFJoditEditor name={"description"} />
        </Box>

        <Box mt={3}>
          <p className="mb-3">Project Thumbnail *</p>
          <Upload
            isAvatar={false}
            text={"Upload thumbnail"}
            file={
              (!defaultValues.thumbnail.includes("undefined") &&
                defaultValues.thumbnail) ||
              image
            }
            name="thumbnail"
            accept="image/*"
            maxSize={1}
            onDrop={handleDropImage}
          />
        </Box>

        <Box mt={3}>
          <RHFSelect
            name="province"
            label="Province *"
            value={values.province || ""}
            onChange={(e) => {
              setValue("province", e.target.value); // Update form state
              handleProvinceChange(e.target.value); // Update districts
            }}
          >
            {CountryData?.provinceList.map((province) => (
              <MenuItem key={province.name} value={province.name}>
                {province.name}
              </MenuItem>
            ))}
          </RHFSelect>
        </Box>

        <Box mt={3}>
          <RHFSelect
            name="district"
            label="District *"
            value={values.district || ""}
          >
            {districts?.map((district) => (
              <MenuItem key={district.name} value={district.name}>
                {district.name}
              </MenuItem>
            ))}
          </RHFSelect>
        </Box>

        <Box mt={3}>
          <p className="mb-3">Project Gallery *</p>
          <RHFUpload
            multiple
            thumbnail
            name="image"
            maxSize={1}
            onDrop={handleDropPhoto}
            onRemove={handleRemoveFile}
          />
        </Box>

        <Stack mt={2} alignItems={"end"}>
          <LoadingButton
            loading={createProjectLoading}
            disabled={createProjectLoading}
            type="submit"
            variant="contained"
            className="!bg-primary w-fit"
          >
            {isEdit ? "Update Project" : "Create Project"}
          </LoadingButton>
        </Stack>
      </FormProvider>
    </Box>
  );
};

export default ProjectForm;
